/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import Radio from 'backbone.radio';
import Ladda from 'ladda';
import Syphon from 'backbone.syphon';
import Backbone from 'backbone';

import Config from '@config/Config';
import { validateForm } from '@/util/ParsleyUtils';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import Session from '@/Session';

import RegistrationInfoForm from '../../forms/RegistrationInfoForm';
import AddCommandView from '../../views/AddCommandView';
import ExternalRegistration from '../../services/ExternalRegistration';

import './RegisterPage.css';
import tmplRegisterPage from './RegisterPage.hbs';

const RegisterPage = View.extend({
  template: tmplRegisterPage,

  className: 'register-page',

  ui: {
    registrationInfoForm: '.registration-info-form',
    addCommandView: '.add-command-view',
    register: '.btn-register',
    btnCACRegister: '.btn-cac-register'
  },

  regions: {
    registrationInfoForm: '@ui.registrationInfoForm',
    addCommandView: '@ui.addCommandView'
  },

  events: {
    'click .btn-return-to-login': 'returnToLogin',
    'click @ui.register': 'register',
    'click @ui.btnCACRegister': 'onClickBtnCacRegister'
  },

  templateContext() {
    return {
      bannerText: Config.getBannerText()
    };
  },

  onRender() {
    this.showChildView('registrationInfoForm', new RegistrationInfoForm());
    this.showChildView(
      'addCommandView',
      new AddCommandView({
        model: new Backbone.Model({
          isProfilePage: false,
          isRegistrationPage: true
        })
      })
    );
  },

  toggleForm(state) {
    this.$el.find(':input').prop('disabled', !state);
    this.getChildView('registrationInfoForm').toggleForm(state);
    this.getChildView('addCommandView').toggleForm(state);
  },

  returnToLogin(e) {
    e.preventDefault();
    Radio.channel('navigate').request('show:ext:login');
  },

  showRegistrationError() {
    showErrorMessage({
      errorCode: 'registrationError',
      errorTitle: 'Registration Error',
      errorMessage:
        'An error occurred during registration. Please reference the requirements for the fields by selecting the question mark icon by each field. If the information you entered meets these standards, please try again or contact an administrator if your issue persists.'
    });
  },

  showUserExistsError() {
    showErrorMessage({
      errorCode: 'registrationError',
      errorTitle: 'Username or Email Taken',
      errorMessage:
        'A user already exists for this username or email. Try again with a different username or email or contact an administrator if your issue persists.'
    });
  },

  showEmailExistsError() {
    showErrorMessage({
      errorCode: 'registrationError',
      errorTitle: 'Email Taken',
      errorMessage:
        'A user already exists for this email. Try again with a different email or contact an administrator if your issue persists.'
    });
  },

  showWeakPasswordError() {
    showErrorMessage({
      errorCode: 'registrationError',
      errorTitle: 'Weak Password',
      errorMessage: 'Your password must not contain three or more consecutive characters from your username.'
    });
  },

  showRegistrationSuccess() {
    showSuccessMessage({
      code: 'registrationSuccess',
      title: 'Request Received',
      message:
        'Your request for registration has been received. An email will be sent once your request has been approved.',
      hideAfter: 5
    });
  },

  async register(e) {
    e.preventDefault();

    const spinner = Ladda.create(this.ui.register[0]);
    spinner.start();
    const formData = Syphon.serialize(this);

    try {
      const [, validCommand] = await Promise.all([
        validateForm(this.ui.registrationInfoForm),
        this.getChildView('addCommandView').validateCommandsList()
      ]);
      if (!validCommand) return;

      this.toggleForm(false);

      formData.commandIds = this.getChildView('addCommandView').getCommandIds();

      const userId = await ExternalRegistration(formData);
      Session.userId = userId;

      this.showRegistrationSuccess();
      Radio.channel('navigate').request('show:ext:login');
    } catch (err) {
      this.toggleForm(true);
      if (err?.responseText?.includes('USER_EXISTS')) {
        this.showUserExistsError();
      } else if (err?.responseText?.includes('MULTIPLE_USERS')) {
        this.showEmailExistsError();
      } else if (err?.responseText?.includes('PASSWORD_TOO_WEAK')) {
        this.showWeakPasswordError();
      } else if (err) {
        this.showRegistrationError();
      }
    } finally {
      spinner.stop();
    }
  },

  onClickBtnCacRegister(e) {
    e.preventDefault();
    Radio.channel('navigate').request('show:ext:cac-register-page');
  }
});

export default RegisterPage;
